/**
 * Lightweight replacement for framer-motion.
 * Renders elements as plain DOM nodes with no entrance/scroll/parallax
 * animations. Interactive behaviour (modals, drawers, carousels) is
 * preserved — they simply appear instantly instead of animating.
 */
import React, { forwardRef, createElement, ReactNode } from "react";

// Props that framer-motion consumes and must not reach the DOM
const MOTION_PROPS = new Set([
  "initial",
  "animate",
  "exit",
  "variants",
  "transition",
  "whileHover",
  "whileTap",
  "whileFocus",
  "whileInView",
  "whileDrag",
  "viewport",
  "layout",
  "layoutId",
  "drag",
  "dragConstraints",
  "dragElastic",
  "dragMomentum",
  "onAnimationComplete",
  "onAnimationStart",
  "onDragStart",
  "onDragEnd",
  "onHoverStart",
  "onHoverEnd",
  "onTap",
  "onTapStart",
  "onTapCancel",
  "onViewportEnter",
  "onViewportLeave",
  "style",
]);

function stripMotionProps(props: Record<string, unknown>) {
  const clean: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (MOTION_PROPS.has(key)) {
      // Keep style, but drop motion values (objects with .on/.get)
      if (key === "style" && props.style && typeof props.style === "object") {
        const style = props.style as Record<string, unknown>;
        const cleanStyle: Record<string, unknown> = {};
        for (const sKey of Object.keys(style)) {
          const v = style[sKey];
          if (v && typeof v === "object" && typeof (v as { get?: unknown }).get === "function") {
            // MotionValue — resolve to its current value so static styles still apply
            cleanStyle[sKey] = (v as { get: () => unknown }).get();
          } else {
            cleanStyle[sKey] = v;
          }
        }
        clean.style = cleanStyle;
      }
      continue;
    }
    clean[key] = props[key];
  }
  return clean;
}

const motionCache = new Map<string, React.ComponentType<Record<string, unknown>>>();

function createPlainComponent(tag: string) {
  const Comp = forwardRef<HTMLElement, Record<string, unknown>>((props, ref) => {
    const { children, ...rest } = props as { children?: ReactNode } & Record<string, unknown>;
    return createElement(tag, { ...stripMotionProps(rest), ref }, children);
  });
  Comp.displayName = `motion.${tag}`;
  return Comp as React.ComponentType<Record<string, unknown>>;
}

export const motion = new Proxy({} as Record<string, React.ComponentType<Record<string, unknown>>>, {
  get(_target, tag: string) {
    if (!motionCache.has(tag)) {
      motionCache.set(tag, createPlainComponent(tag));
    }
    return motionCache.get(tag);
  },
});

export const AnimatePresence = forwardRef<HTMLElement, { children?: ReactNode }>(
  ({ children }, _ref) => <>{children}</>
);
AnimatePresence.displayName = "AnimatePresence";

// Hooks — return static values so scroll/parallax effects become inert
export const useScroll = () => ({
  scrollY: { get: () => 0, on: () => () => {} },
  scrollYProgress: { get: () => 0, on: () => () => {} },
  scrollX: { get: () => 0, on: () => () => {} },
  scrollXProgress: { get: () => 0, on: () => () => {} },
});

export const useTransform = (_value: unknown, _input?: unknown, output?: unknown[]) =>
  ({ get: () => (Array.isArray(output) ? output[0] : 0), on: () => () => {} });

export const useSpring = (value: unknown) => {
  if (value && typeof value === "object" && typeof (value as { get?: unknown }).get === "function") {
    return value;
  }
  return { get: () => value ?? 0, on: () => () => {} };
};

export const useInView = () => true;

export const useMotionValue = (initial: unknown) => ({
  get: () => initial,
  set: () => {},
  on: () => () => {},
});

export const useAnimation = () => ({
  start: () => Promise.resolve(),
  set: () => {},
  stop: () => {},
});

export const useMotionValueEvent = () => {};

export const useReducedMotion = () => true;
